import React, { useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import toast from "react-hot-toast";
import { DateDisplay } from "../../../components";
import { IconButton } from "../../../components/buttons";
import { ConfirmModal } from "../../../components/modals";
import useOpenDocument from "../../../hooks/useOpenDocument";
import UploadDocumentModal from "../../uploaddocumentmodal";
import useClientFeeAgreements from "../hooks/useClientFeeAgreements";
import useClientFeeSignatories from "../hooks/useClientFeeSignatories";
import useClientPersons from "../hooks/useClientPersons";
import { DateInput, FormSelect } from "./../../../components/forms/";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";
import { CenterFormColumnComponent, ThemedIcon, ThemedSpan } from "../../../components/utilities";
import { Overlay, OverlayTrigger, Tooltip } from "react-bootstrap";
import FeeAgreementStatusActionMenuItem from "./FeeAgreementStatusActionMenuItem";
import { openServiceAgreementUpload } from "./../../../components/modals/service-agreement-upload";

const ServiceAgreementProofs = ({ masterAccountId, clientServiceAgreementId, feeAgreements = [], feeSignatories = [], canEdit, isPreviewed, loadingLines = 3 }) => {

    const { clientPersons } = useClientPersons(masterAccountId);
    const { openDocument, isFetching: isOpening } = useOpenDocument();
    const { previewClientConsent,
        publishClientConsent,
        isPublishing,
        updateFeeAgreement,
        deleteClientFeeAgreement
    } = useClientFeeAgreements(masterAccountId);

    const { updateClientFeeSignatoriesMappings } = useClientFeeSignatories(masterAccountId);

    const onFieldChanged = (clientFeeAgreementId, property, value) => updateFeeAgreement(clientFeeAgreementId, property, value);

    const onClientFeeSignatorySelected = (values) => updateClientFeeSignatoriesMappings(values);

    const [showUploadDocument, setShowUploadDocument] = useState(false);
    const [selectedFeeAgreement, setSelectedFeeAgreement] = useState(null);

    const handleOnDocumentUploaded = (r) => {
        if (r && r.documentId != null) {
            //Post up to update the fee agreement 
            onFieldChanged(selectedFeeAgreement,"evidenceDocumentId", r.documentId);
        }
    };

    const uploadEvidence = (feeAgreement) => {
        setSelectedFeeAgreement(feeAgreement.clientFeeAgreementId);
        setShowUploadDocument(true);
    };

    const handleOnDocumentOpened = (documentId) => {
        if (!documentId || isOpening) {
            return;
        }

        openDocument(documentId);
    };

    const handleOnPublishConsentClicked = () => {
        toast.promise(publishClientConsent(), {
            loading: `Attempting to publish client consent...`,
            success: (data) => {
                return `The client consent form has been published`;
            },
            error: (err) => <div>Failed to publish Client Consent - {err?.data?.message}</div>
        });
    };

    const handleOnPreviewConsentClicked = () => {
        toast.promise(previewClientConsent(), {
            loading: `Building client consent preview`,
            success: (data) => {
                var bytesPdf = Uint8Array.from(window.atob(data), c => c.charCodeAt(0))

                var file = new Blob([bytesPdf], { type: 'application/pdf' });
                var fileURL = URL.createObjectURL(file);

                window.open(fileURL, 'Client Consent');
                return `Client consent is open in a new window.`;
            },
            error: (err) => <div>Failed to build Client Consent Preview - {err?.data}</div>

        });
    };

    const handleConsentDeleted = (feeAgreementId) => {
        openConfirmModal({
            dialogTitle: "Delete published consent",
            message: "Are you sure you would like to delete the published consent? This action cannot be undone."
        }).then(
            (proceed) => {
                if (proceed)
                    deleteClientFeeAgreement(feeAgreementId);
            }
        )
    }

    const showConfirmModal = () => {
        openConfirmModal({
            dialogTitle: "Publish Client Consent Form",
            message: "Are you sure you would like to publish the client consent form?"
        }).then(
            (proceed) => {
                if (proceed)
                    handleOnPublishConsentClicked()
            }
        )
    }

    const canEditfeeAgreement = (feeAgreement) => {

        if(feeAgreement.signedDate != null) return false;
        if(feeAgreement.status !== 0) return false;

        return true;

    }

    const canPublishAgreement = 
    !feeAgreements?.some(x => x.signedDate == null) 
    && !isPublishing;

    return (
        <div>
            <UploadDocumentModal
                handleClose={() => setShowUploadDocument(false)}
                onDocumentUploaded={handleOnDocumentUploaded}
                show={showUploadDocument}
            />
            <div className="row mb-4">
                <div className="col">
                    <FormSelect
                        label="Required Signatures"
                        placeholder="Required Signatures"
                        isMulti={true}
                        options={clientPersons}
                        defaultValue={Array.isArray(feeSignatories) ? feeSignatories.map((el) => el.clientId) : []}
                        onBlur={(values) => onClientFeeSignatorySelected(values)}
                        isDisabled={!canEdit()}
                    />
                </div>
            </div>
            <div className="row mb-4">
                <div className="col-2">
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={() => handleOnPreviewConsentClicked()}
                        disabled={feeSignatories.length === 0 || feeSignatories === null || !canEdit()}
                    >
                        Preview Agreement
                    </Button>
                </div>
                <div className="col-2">
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={showConfirmModal}
                        disabled={feeSignatories.length === 0 || feeSignatories === null || isPublishing || !canEdit() || !isPreviewed}
                    >
                        Publish Agreement
                    </Button>
                </div>
                {!isPreviewed && <div className="col-md-auto my-auto">
                     <OverlayTrigger overlay={
                        <Tooltip>Agreement has not been previewed</Tooltip>
                    }>
                        <ThemedIcon variant="warning" icon="fa-circle-exclamation" size="2xl" />
                    </OverlayTrigger>
                </div>}
                {isPreviewed && <div className="col-md-auto my-auto">
                    <ThemedIcon variant="success" icon="fa-circle-check" size="2xl" />
                </div>}
            </div>
            {(feeAgreements.length > 0) && (
                <div className="row mb-2 fw-bold">
                    <div className="col-2">Date Published</div>
                    <div className="col-2">Status</div>
                    <div className="col-2">Authorisation Method</div>
                    <div className="col-2">Evidence Uploaded</div>
                    <div className="col-2">Date Signed</div>
                    <div className="col-md-auto"></div>
                </div>
            )}
            {feeAgreements && feeAgreements.length === 0 && (
                    <div className="row">
                        <div className="col">
                            There are no published agreements.  Publish an agreement above to update the evidence.
                        </div>
                    </div>
                )}
            {feeAgreements && feeAgreements.map((el, i) => (
                    <div className="row mb-2" key={el.clientFeeAgreementId}>
                        <div className="col-2">
                            <span>{el.publishedByName}</span><br/>
                            <ThemedSpan variant="muted" className="fs-smaller">
                                <DateDisplay format="DD/MM/YYYY">
                                    {el.publishedDate}
                                </DateDisplay>
                            </ThemedSpan>
                            
                        </div>
                        <div className="col-2">
                            <span>                                                           
                                {el.statusDescription}
                            </span><br/>
                            <ThemedSpan variant="muted" className="fs-smaller">

                                <DateDisplay format="DD/MM/YYYY">
                                    {el.statusDate}
                                </DateDisplay>
                            </ThemedSpan>
                        </div>
                        <div className="col-2">
                            <FormSelect
                                defaultValue={el.authorisationMethod}
                                options={[
                                    { label: "Physical Signature", value: 0 },
                                    { label: "Electronic Signature", value: 1 },
                                    { label: "Email Confirmation", value: 2 }
                                ]}
                                isDisabled={!canEditfeeAgreement(el)}
                                onBlur={(values) => onFieldChanged(el.clientFeeAgreementId, 'authorisationMethod', values.value)}
                            />
                        </div>
                        <div className="col-2">
                            {el.uploadedDate != null && <>
                            <span>                                                           
                                {el.uploadedByName}
                            </span><br/>
                            <ThemedSpan variant="muted" className="fs-smaller">
                                <DateDisplay
                                    format="DD/MM/YYYY"
                                    >
                                    {el.uploadedDate}
                                </DateDisplay>
                            </ThemedSpan></>}
                            {el.uploadedDate == null && <>
                                <span>-</span>
                            </>}
                        </div>
                        <div className="col-2 my-auto">
                               {/* <DateInput
                                    value={el.signedDate}
                                    isClearable={true}
                                    onBlur={(value) => onFieldChanged(el.clientFeeAgreementId, 'signedDate', value)}
                                    disabled={!canEditfeeAgreement(el)}
                                /> */}
                            {el.signedDate != null && <DateDisplay
                                format="DD/MM/YYYY"
                            >
                                {el.signedDate}
                            </DateDisplay>}
                            {el.signedDate == null && <>
                                <span>-</span>
                            </>}
                        </div>
                        <div className="col-md-auto my-auto">
                            <Dropdown className="w-100">
                                <Dropdown.Toggle as={ThemedIcon} icon="fa-ellipsis" className="has-pointer" />
                                <Dropdown.Menu>
                                        <Dropdown.Header>Documents</Dropdown.Header>
                                        <Dropdown.Item
                                            onClick={() => handleOnDocumentOpened(el.originalDocumentId)}>
                                                View Agreement
                                        </Dropdown.Item>
                                        {el.evidenceDocumentId == null && 
                                            <Dropdown.Item 
                                                onClick={() => openServiceAgreementUpload(el.clientFeeAgreementId)} 
                                                disabled={el.status !== 1 && el.status !== 0}
                                            >
                                                    Upload Evidence
                                            </Dropdown.Item>
                                        }
                                        {el.evidenceDocumentId != null &&
                                            <Dropdown.Item 
                                                onClick={() => handleOnDocumentOpened(el.evidenceDocumentId)}>
                                                    View Evidence
                                            </Dropdown.Item>
                                        }
                                        <Dropdown.Divider />
                                        <Dropdown.Header>Set Status</Dropdown.Header>
                                        <FeeAgreementStatusActionMenuItem
                                            currentId = {el.clientFeeAgreementId}
                                            label="Not Taken Up"
                                            status={2}
                                            isCurrent={el.status === 2}
                                            isDisabled={el.signedDate != null || el.status !== 0}
                                            updateStatus={() => {
                                                openConfirmModal({
                                                    allowConfirmButton: true,
                                                    dialogTitle: "Updating Status",
                                                    message: "Updating the status will set the service terms back to drafting and require a new preview and publish"
                                                })
                                                .then((proceed) => {
                                                    if (proceed) onFieldChanged(el.clientFeeAgreementId, 'status', 2);
                                                })
                                            }}
                                        />
                                        <FeeAgreementStatusActionMenuItem
                                            currentId = {el.clientFeeAgreementId}
                                            label="Amended Terms Required"
                                            status={3}
                                            isCurrent={el.status === 3}
                                            isDisabled={el.signedDate != null || el.status !== 0}
                                            updateStatus={() => {
                                                openConfirmModal({
                                                    allowConfirmButton: true,
                                                    dialogTitle: "Updating Status",
                                                    message: "Updating the status will set the service terms back to drafting and require a new preview and publish"
                                                })
                                                .then((proceed) => {
                                                    if (proceed) onFieldChanged(el.clientFeeAgreementId, 'status', 3);
                                                })
                                            }}
                                        />
                                        <FeeAgreementStatusActionMenuItem
                                            currentId={el.clientFeeAgreementId}
                                            label="Correction Required"
                                            status={4}
                                            isCurrent={el.status === 4}
                                            isDisabled={el.signedDate != null || el.status !== 0}
                                            updateStatus={() => {
                                                openConfirmModal({
                                                    allowConfirmButton: true,
                                                    dialogTitle: "Updating Status",
                                                    message: "Updating the status will set the service terms back to drafting and require a new preview and publish"
                                                })
                                                .then((proceed) => {
                                                    if (proceed) onFieldChanged(el.clientFeeAgreementId, 'status', 4);
                                                })
                                            }}
                                        />
                                        {/* <Dropdown.Divider />
                                        <Dropdown.Item disabled={el.signedDate != null} onClick={() => handleConsentDeleted(el.clientFeeAgreementId)}>Delete Agreement</Dropdown.Item> */}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                ))}
        </div>
    );
};

export default ServiceAgreementProofs;
